import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

// The camera, extracted from the former Archive page (`handleStartCamera`).
//
// It opens a MediaStream over the received <video> and closes it on stop or
// on unmount. The constraints are the usual ones: on mobile the rear camera
// is requested at 1080p; on desktop, whatever is available.
//
// The `getUserMedia` error (permission denied, no camera) is returned instead
// of being shown with `alert`, so that the calling component paints it in
// French where appropriate.

export type CameraError = 'denied' | 'unavailable' | 'unknown';

const constraintsFor = (rearCamera: boolean): MediaStreamConstraints => ({
  audio: false,
  video: rearCamera
    ? {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1920, max: 2560 },
        height: { ideal: 1080, max: 1440 },
        aspectRatio: { ideal: 1.777777778 },
      }
    : true,
});

const classify = (err: unknown): CameraError => {
  const name = err instanceof Error ? err.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied';
  if (
    name === 'NotFoundError' ||
    name === 'OverconstrainedError' ||
    name === 'NotReadableError'
  ) {
    return 'unavailable';
  }
  return 'unknown';
};

export const useCameraStream = (videoRef: RefObject<HTMLVideoElement>) => {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<CameraError | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const stop = useCallback(() => {
    const current = streamRef.current;
    if (current) {
      current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    const video = videoRef.current;
    if (video && video.srcObject) {
      video.srcObject = null;
    }
    setStream(null);
  }, [videoRef]);

  // Resolves once the video already has metadata (dimensions), which is what
  // is needed to capture a frame or for the QR reader to start.
  const start = useCallback(
    async (rearCamera: boolean): Promise<MediaStream | null> => {
      const video = videoRef.current;
      if (!video) return null;
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('unavailable');
        return null;
      }

      stop();
      setError(null);

      try {
        const opened = await navigator.mediaDevices.getUserMedia(
          constraintsFor(rearCamera),
        );
        streamRef.current = opened;
        video.muted = true;
        video.setAttribute('playsinline', 'true');
        video.srcObject = opened;

        await new Promise<void>((resolve) => {
          if (video.readyState >= 1) {
            resolve();
            return;
          }
          video.addEventListener('loadedmetadata', () => resolve(), {
            once: true,
          });
        });

        await video.play().catch(() => {});
        setStream(opened);
        return opened;
      } catch (err) {
        setError(classify(err));
        return null;
      }
    },
    [videoRef, stop],
  );

  useEffect(() => stop, [stop]);

  return { stream, error, start, stop };
};
